import { Locale } from "@/i18n/config";
import { usePathname, useRouter } from "@/i18n/navigation";
import { setUserLocale } from "@/services/locale";
import { Check } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useTransition } from "react";
import { GateLoader } from "../LangLoader";
import { useSearchParams } from "next/navigation";
import {
  getLastSearchRequestFromLocalStorage,
  getPartnerCode,
  setLastSearchRequestToLocalStorage,
} from "@/app/utils/services/service/localStorageService";
import { getFilteredParams } from "@/app/common/utils/getFilteredParams";
import axios from "axios";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import type { GeoLocation } from "@/types";
import { flags } from "@/constants/lang";

type Props = {
  label: string;
  locale: string;
  noSelector?: boolean;
};

export const LangItem = ({ label, locale, noSelector = false }: Props) => {
  const [isPending, startTransition] = useTransition();
  const currentLocale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const { userDetails, getConnectedWallet } = useWalletService();

  const createSearchRequest = () => {
    const lastSearchRequest =
      getLastSearchRequestFromLocalStorage(currentLocale);
    if (!lastSearchRequest) return null;

    const geolocation: GeoLocation = { lat: undefined, lng: undefined };

    return {
      id: lastSearchRequest.id,
      from: lastSearchRequest.from,
      to: lastSearchRequest.to,
      numberOfChildren: Number(lastSearchRequest.numberOfChildren),
      numberOfAdults: Number(lastSearchRequest.numberOfAdults),
      numberOfInfants: Number(lastSearchRequest.numberOfInfants),
      city: lastSearchRequest.city,
      country: lastSearchRequest.country,
      currency: lastSearchRequest.currency || "USD",
      location: geolocation,
      locationName: undefined,
      destination: lastSearchRequest.destination,
      wallet: userDetails?.walletId?.trim(),
    };
  };

  const searchForHotels = async (hotelRequest: any) => {
    const body = { ...hotelRequest, partnerCode: getPartnerCode() };
    setLastSearchRequestToLocalStorage(hotelRequest, locale);

    const { data } = await axios.post(`/api/search`, body);
    const filteredParams = getFilteredParams(searchParams);
    window.location.href = `/${locale}/results?searchId=${data?.searchId}&${filteredParams.toString()}&title=${hotelRequest.destination}`;
  };

  const createHotelRequestToken = async (
    walletAddress: string,
    hotelRequest: any
  ) => {
    const data = {
      ...hotelRequest,
      partnerCode: getPartnerCode(),
      connectedWallet: getConnectedWallet(),
    };

    const res = await axios.post(`/api/generateToken`, data, {
      withCredentials: true,
    });
    if (res?.data?.tokenId) {
      setLastSearchRequestToLocalStorage(hotelRequest, locale);
      const tokenId = res.data.tokenId;
      const filteredParams = getFilteredParams(searchParams);
      window.location.href = `/${locale}/results?tokenId=${tokenId}&${filteredParams.toString()}&title=${hotelRequest.destination}`;
    }
  };

  const fetchAndNavigate = async () => {
    const hotelRequest = createSearchRequest();
    if (!hotelRequest) return;

    if (userDetails?.walletId) {
      await createHotelRequestToken(userDetails.walletId.trim(), hotelRequest);
    } else {
      await searchForHotels(hotelRequest);
    }
  };

  function onChangeLocale() {
    const pathWithoutLocale = pathname.replace(
      /^\/[a-z]{2}(?:-[A-Z]{2})?\//,
      "/"
    );

    const isResultsPage =
      pathWithoutLocale === "/results" ||
      pathWithoutLocale.startsWith("/results/details");

    startTransition(async () => {
      setUserLocale(locale as Locale);

      if (isResultsPage) {
        // Re-run fetchAndNavigate to handle API + redirect with locale
        await fetchAndNavigate();
      } else {
        // Normal locale change for other pages
        const query = searchParams.toString();
        const urlWithParams = query ? `${pathname}?${query}` : pathname;
        router.push(urlWithParams, { locale });
      }
    });
  }

  const isSelected = currentLocale === locale;

  return (
    <button
      type="button"
      onClick={onChangeLocale}
      className="flex gap-2 p-2 items-center justify-between w-full rounded-none font-switzer text-base font-semibold"
      // disabled={isSelected}
    >
      <span className="flex gap-2 items-center">
        <Image //@ts-ignore
          src={`/assets/v2/icons/flags/${flags[locale]}.svg`}
          height={20}
          alt="icon"
          width={20}
        />
        <span className="text-nowrap">{label}</span>
      </span>
      {!noSelector && !isPending && isSelected && (
        <Check className="text-sleap-purple-300 block" />
      )}
      {isPending && <GateLoader />}
    </button>
  );
};
